import clientSvc from '@efex-services/client/clientSvc';
import { IValidateContactEmailCamelCase } from '@efex-services/client/types';
import { DEBOUNCE_TIME } from '@main-hooks/useDebounce';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useApiCall } from '@efexteam/use-api-call';
import {
  IAddMemberFormData,
  IContact,
  IUseEmailValidationResponse,
} from '../types';

const useEmailValidation = ({
  canContinue,
}: {
  canContinue: (value: boolean) => void;
}): IUseEmailValidationResponse => {
  const { control, setValue } = useFormContext<Partial<IAddMemberFormData>>();

  const [contacts, setContacts] = useState<IContact[]>([]);
  const [results, setResults] = useState<IValidateContactEmailCamelCase[]>([]);
  const [timer, setTimer] = useState<number | undefined>(undefined);
  const [selectedContact, setSelectedContact] = useState<IContact | undefined>(
    undefined
  );

  const {
    loading,
    error,
    invoke: validateContactEmail,
  } = useApiCall((email?: string) => {
    if (!email) {
      throw new Error('Email is required');
    }
    return clientSvc.validateContactEmail(email);
  });

  const handleChange = useCallback(
    (_: any, contact: any) => {
      if (!contact?.label) {
        return;
      }
      const contactFiltered =
        contacts.find((opt) => opt.value === contact.value) || contact;
      setSelectedContact(contactFiltered);
      setValue('email', contactFiltered);
    },
    [contacts, setValue]
  );

  const searchContactByEmail = (email: string) => {
    if (email.length === 0) {
      setContacts([]);
      setValue('email', { label: '', value: undefined });
      return;
    }
    setContacts([]);
    const defaultContact = { label: email, value: 1000000000 };
    if (email.match(/\S+@\S+\.\S+/)) {
      setResults([]);
      setContacts([defaultContact]);
      setValue('email', defaultContact);
    }
    validateContactEmail(email);
  };

  const shouldPerformSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (timer) {
      clearInterval(timer);
    }
    setTimer(
      window.setTimeout(() => {
        searchContactByEmail(event.target.value);
      }, DEBOUNCE_TIME)
    );
  };

  useEffect(() => {
    if (contacts.length === 1) handleChange({}, contacts[0]);
  }, [contacts, handleChange]);

  useEffect(() => {
    if (error) {
      canContinue(false);
      setResults([]);
    } else {
      canContinue(true);
    }
  }, [error, canContinue]);

  return {
    contacts,
    results,
    loading,
    error,
    selectedContact,
    control,
    handleChange,
    shouldPerformSearch,
  };
};

export default useEmailValidation;
